import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowUpRight, Play } from "lucide-react";

const HERO_SLIDES = [
  { type: "video", src: "/hero/clip-01.mp4" },
  { type: "img",   src: "/hero/01.jpg" },
  { type: "img",   src: "/hero/02.jpg" },
  { type: "img",   src: "/hero/11.jpg" }, // Biswajit × Irrfan Khan
  { type: "img",   src: "/hero/03.jpg" },
  { type: "img",   src: "/hero/12.jpg" },
  { type: "img",   src: "/hero/04.jpg" },
  { type: "video", src: "/hero/clip-02.mp4" },
  { type: "img",   src: "/hero/13.jpg" },
  { type: "img",   src: "/hero/05.jpg" },
  { type: "img",   src: "/hero/14.jpg" },
  { type: "img",   src: "/hero/06.jpg" },
  { type: "img",   src: "/hero/15.jpg" },
  { type: "img",   src: "/hero/07.jpg" },
  { type: "img",   src: "/hero/08.jpg" },
  { type: "img",   src: "/hero/09.jpg" },
  { type: "img",   src: "/hero/10.jpg" },
];
const IMG_MS = 2200;
const VIDEO_MS = 4500; // short motion sting dwell

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

function HeroSlideshow() {
  const [index, setIndex] = useState(0);
  const current = HERO_SLIDES[index];
  const dwell = current.type === "video" ? VIDEO_MS : IMG_MS;

  useEffect(() => {
    const id = setTimeout(
      () => setIndex((i) => (i + 1) % HERO_SLIDES.length),
      dwell
    );
    return () => clearTimeout(id);
  }, [index, dwell]);

  // Pre-cache next frame so the swap is instant
  useEffect(() => {
    const next = HERO_SLIDES[(index + 1) % HERO_SLIDES.length];
    if (next.type === "img") {
      const im = new Image();
      im.src = next.src;
    }
  }, [index]);

  return (
    <div className="relative mx-auto w-full max-w-[460px]">
      {/* Film-strip left rail — editorial detail */}
      <div className="pointer-events-none absolute -left-6 top-6 bottom-6 hidden flex-col justify-between md:flex">
        {HERO_SLIDES.map((_, i) => (
          <span
            key={i}
            className={`h-2.5 w-2.5 rounded-sm transition-colors duration-500 ${
              i === index ? "bg-[#CBFF3A]" : "bg-white/15"
            }`}
          />
        ))}
      </div>

      {/* Frame */}
      <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10 bg-[#0d0d0f]">
        {HERO_SLIDES.map((s, i) => {
          const active = i === index;
          if (s.type === "video") {
            return (
              <video
                key={s.src}
                src={s.src}
                muted
                playsInline
                autoPlay
                loop
                preload="auto"
                aria-hidden={!active}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1000ms] ease-out ${
                  active ? "opacity-100" : "opacity-0"
                }`}
              />
            );
          }
          // For stills, place a blurred background copy under an un-cropped
          // `object-contain` foreground so every photo is shown in full while
          // the frame stays filled with the same colour palette.
          return (
            <div
              key={s.src}
              aria-hidden={!active}
              className={`absolute inset-0 transition-opacity duration-[1000ms] ease-out ${
                active ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={s.src}
                alt=""
                loading={i < 2 ? "eager" : "lazy"}
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-70 blur-xl"
                aria-hidden
              />
              <img
                src={s.src}
                alt=""
                loading={i < 2 ? "eager" : "lazy"}
                className="relative z-[1] h-full w-full object-contain transition-transform ease-out"
                style={{
                  transform: active ? "scale(1.02)" : "scale(1)",
                  transitionDuration: "2200ms",
                }}
              />
            </div>
          );
        })}

        {/* Scrim + grain */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-black/25" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)",
          }}
        />

        {/* Camera reticles */}
        <span className="absolute left-3 top-3 h-5 w-5 border-l-2 border-t-2 border-[#CBFF3A]" />
        <span className="absolute right-3 top-3 h-5 w-5 border-r-2 border-t-2 border-[#CBFF3A]" />
        <span className="absolute bottom-3 left-3 h-5 w-5 border-b-2 border-l-2 border-[#CBFF3A]" />
        <span className="absolute bottom-3 right-3 h-5 w-5 border-b-2 border-r-2 border-[#CBFF3A]" />

        {/* Top-left tag */}
        <div className="absolute left-5 top-5 flex items-center gap-2">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span className="font-hud text-[9px] uppercase tracking-[0.25em] text-white/80">
            {current.type === "video" ? "Rec · Motion" : "Live · 25mm"}
          </span>
        </div>

        {/* Top-right index */}
        <div className="absolute right-5 top-5 flex items-center gap-2 rounded-full border border-[#CBFF3A]/40 bg-black/55 px-2.5 py-0.5 font-hud text-[9px] uppercase tracking-[0.25em] text-[#CBFF3A] backdrop-blur-sm">
          {String(index + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
        </div>

        {/* Bottom caption */}
        <div className="absolute inset-x-5 bottom-5 flex items-end justify-between">
          <div>
            <p className="font-hud text-[9px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
              On Set · Behind the Lens
            </p>
            <p className="mt-1 font-display text-xs italic text-white/70">
              — Biswajit Dey
            </p>
          </div>
          <span className="font-hud text-[9px] uppercase tracking-[0.25em] text-white/50">
            {current.type === "video" ? "24 fps · 1/50" : "f/2.8 · 1/50"}
          </span>
        </div>

        {/* Progress bar */}
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-white/10">
          <div
            key={index}
            className="h-full bg-[#CBFF3A]"
            style={{ animation: `heroProgress ${dwell}ms linear forwards` }}
          />
        </div>
      </div>

      {/* Subtle lime underline */}
      <div className="mt-5 flex items-center gap-3">
        <span className="font-hud text-[10px] uppercase tracking-[0.3em] text-white/40">
          Portfolio Reel
        </span>
        <span className="h-[1px] flex-1 bg-gradient-to-r from-[#CBFF3A]/40 to-transparent" />
        <span className="font-hud text-[10px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
          2024
        </span>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const go = (e, href) => {
    e.preventDefault();
    lenis?.scrollTo(href, { offset: -20 });
  };
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <style>{`
        @keyframes heroProgress {
          from { transform: scaleX(0); transform-origin: left center; }
          to   { transform: scaleX(1); transform-origin: left center; }
        }
      `}</style>

      {/* Ambient background: deep gradient + lime light-leak */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top_left,rgba(203,255,58,0.09),transparent_55%),linear-gradient(180deg,#0a0a0b,#07070a)]" />
      <div className="pointer-events-none absolute -right-20 top-1/4 z-0 h-[520px] w-[520px] rounded-full bg-[#CBFF3A]/[0.07] blur-[130px]" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0 1px, transparent 1px 3px)",
        }}
      />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-6 pt-28 pb-20 md:px-16 md:pt-32 lg:grid-cols-[1.25fr_1fr] lg:gap-16"
      >
        {/* LEFT — editorial text block */}
        <div className="relative">
          {/* Vertical signature rail — left */}
          <div className="pointer-events-none absolute -left-8 top-0 hidden h-full items-end md:flex">
            <span className="rotate-180 font-hud text-[10px] uppercase tracking-[0.4em] text-white/35 [writing-mode:vertical-rl]">
              Biswajit Dey · Portfolio · MMXXIV
            </span>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-6 flex items-center gap-3 font-hud text-[11px] uppercase tracking-[0.3em] text-white/55"
          >
            <span className="h-[1px] w-10 bg-[#CBFF3A]" />
            Creative Marketer · Video Producer
          </motion.div>

          <h1 className="font-display text-5xl font-500 leading-[1.02] tracking-tight text-[#ECEAE3] sm:text-6xl lg:text-[4.4rem] xl:text-[5rem]">
            <span className="mask-line">
              <motion.span
                custom={0}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block"
              >
                Telling Stories
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span
                custom={1}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block"
              >
                that <span className="italic text-[#CBFF3A] text-glow">Sell.</span>
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span
                custom={2}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="mt-2 block text-3xl font-400 text-white/70 sm:text-4xl lg:text-5xl"
              >
                Producing content that{" "}
                <span className="italic text-[#ECEAE3]">matters.</span>
              </motion.span>
            </span>
          </h1>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "4rem" }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-8 h-[1px] bg-[#CBFF3A]"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-6 max-w-xl font-body text-base font-300 leading-relaxed text-white/60"
          >
            A synthesis of data-driven marketing strategy and high-fidelity video
            production. I don't just make films — I create commercial assets that
            define brands and dominate attention.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="hero-hire-btn"
              className="group flex items-center gap-2 rounded-full bg-[#CBFF3A] px-9 py-4 font-hud text-xs uppercase tracking-[0.15em] text-black electric-glow transition-transform duration-300 hover:scale-105"
            >
              Hire Me
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#work"
              onClick={(e) => go(e, "#work")}
              data-testid="hero-reel-btn"
              className="group flex items-center gap-3 rounded-full border border-white/20 px-9 py-4 font-hud text-xs uppercase tracking-[0.15em] text-[#ECEAE3] transition-colors duration-300 hover:border-[#CBFF3A] hover:text-[#CBFF3A]"
            >
              <Play className="h-4 w-4 fill-current" />
              View Reel
            </a>
          </motion.div>
        </div>

        {/* RIGHT — slideshow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative"
        >
          <HeroSlideshow />
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <span className="font-hud text-[10px] uppercase tracking-[0.3em] text-white/60">
          Scroll
        </span>
        <div className="h-10 w-[1px] bg-gradient-to-b from-[#CBFF3A] to-transparent" />
      </motion.div>
    </section>
  );
}
